// Logique des écrans de supervision (lot L6-A) : libellés, tons d'état, formatage des valeurs (montants
// marqués « (fictif) » pour une collectivité fictive), carte du répertoire (échelles Gabon et provinces,
// marqueurs placés au centre de la limite rapprochée, jamais à une position inventée).
import { LIBELLES_ETAT_VALEUR, type EtatValeurSupervision } from '@cc/contrats';
import { centreEmprise, emprise, type MarqueurCarte, type TonEtat, type Territoire } from '@cc/ui';
import { normaliser, type Limite, type NomFichier, type Presentation } from '../territoire/donnees';

export const LIBELLES_DEPLOIEMENT: Record<string, string> = {
  non_deployee: 'Non déployée',
  en_preparation: 'En préparation',
  active: 'Active',
  suspendue: 'Suspendue',
};

export const TON_DEPLOIEMENT: Record<string, TonEtat> = { non_deployee: 'neutre', en_preparation: 'attente', active: 'ok', suspendue: 'retard' };

export const TON_ETAT_VALEUR: Record<EtatValeurSupervision, TonEtat> = {
  disponible: 'ok',
  indisponible: 'neutre',
  masque: 'attente',
  non_active: 'neutre',
  non_partage: 'attente',
  non_renseigne: 'neutre',
  source_absente: 'retard',
};

export const libelleEtat = (e: string) => LIBELLES_ETAT_VALEUR[e as EtatValeurSupervision] ?? e;

export const LIBELLES_TYPE: Record<string, string> = { commune: 'Commune', conseil_departemental: 'Conseil départemental' };

export const PERIMETRES: Record<string, string> = { national: 'national', province: 'provincial', departement: 'départemental' };

const nombre = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

/** Valeur affichée : « indisponible » plutôt que zéro, unité et mention fictive pour les montants. */
export function valeur(v: unknown, unite: string, fictive = false): string {
  if (v === null || v === undefined) return 'indisponible';
  const n = Number(v);
  if (unite === 'montant') return `${nombre.format(n)} F CFA${fictive ? ' (fictif)' : ''}`;
  if (unite === 'taux') return `${nombre.format(n)} %`;
  return nombre.format(n);
}

export const dateCalcul = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Libreville' }) : 'jamais calculée';

/** Mois courant à Libreville (UTC+1) au format AAAA-MM. */
export function moisCourant(maintenant = new Date()): string {
  return new Date(maintenant.getTime() + 3600_000).toISOString().slice(0, 7);
}

/** Échelles de la carte nationale : Gabon puis chaque province (limites OpenStreetMap). */
export function echellesNationales(limites: Partial<Record<NomFichier, Limite[]>>): Territoire[] {
  const pays = (limites.pays ?? [])[0];
  const provinces = limites.provinces ?? [];
  const voisin = (l: Limite) => ({ id: l.properties.code, libelle: l.properties.nom, geometrie: l.geometry, indicatif: l.properties.indicatif });
  return [
    { id: 'gabon', niveau: 'pays', libelle: 'Gabon', geometrie: pays?.geometry ?? null, indicatif: false, source: pays ? 'OpenStreetMap (ODbL)' : null, voisins: provinces.map(voisin) },
    ...provinces.map((p) => ({
      id: p.properties.code,
      niveau: 'province' as const,
      libelle: p.properties.nom,
      geometrie: p.geometry,
      indicatif: false,
      source: 'OpenStreetMap (ODbL)',
      voisins: provinces.filter((x) => x !== p).map(voisin),
    })),
  ];
}

export interface CollectiviteCarte {
  collectivite_id: string;
  code_court: string;
  nom: string;
  type: string;
  fictive: boolean;
  statut_deploiement: string;
}

/**
 * Marqueurs au centre de la limite de la collectivité : rapprochement par le nom pour une collectivité
 * réelle, territoire d'exemple cité dans presentation.json pour une collectivité fictive. Sans limite,
 * aucune position n'est inventée : la collectivité est listée sans marqueur.
 */
export function marqueursCollectivites(
  lignes: CollectiviteCarte[],
  limites: Partial<Record<NomFichier, Limite[]>>,
  presentation: Presentation | null,
): { marqueurs: MarqueurCarte[]; sansPosition: string[]; exemples: string[] } {
  const communes = limites.communes ?? [];
  const departements = limites.departements ?? [];
  const marqueurs: MarqueurCarte[] = [];
  const sansPosition: string[] = [];
  const exemples: string[] = [];
  for (const c of lignes) {
    const nom = normaliser(c.nom);
    let limite = c.type === 'conseil_departemental' ? departements.find((d) => normaliser(d.properties.nom) === nom) : communes.find((l) => normaliser(l.properties.nom) === nom);
    const correspondance = c.fictive ? presentation?.collectivites[c.code_court] : undefined;
    if (!limite && correspondance) {
      limite = correspondance.commune ? communes.find((l) => l.properties.code === correspondance.commune) : departements.find((d) => d.properties.code === correspondance.departement);
      if (limite) exemples.push(c.nom);
    }
    if (!limite) {
      sansPosition.push(c.nom);
      continue;
    }
    const [longitude, latitude] = centreEmprise(emprise(limite.geometry));
    marqueurs.push({
      id: c.collectivite_id,
      numero: marqueurs.length + 1,
      longitude,
      latitude,
      libelle: `${c.nom}${c.fictive ? ' (fictive)' : ''} · ${LIBELLES_DEPLOIEMENT[c.statut_deploiement] ?? c.statut_deploiement}`,
      ton: c.statut_deploiement === 'active' ? 'ouvert' : c.statut_deploiement === 'suspendue' ? 'retard' : 'autre',
    });
  }
  return { marqueurs, sansPosition, exemples };
}
